import { get } from "lodash-es";
import React, { FC, useState } from "react";
import { useParams } from "react-router-dom";

import { Alert, Container, Paper } from "@mui/material";
import {
  GridCellParams,
  GridColDef,
  getGridStringOperators,
} from "@mui/x-data-grid-pro";

import DataGridPro from "../../components/DataGridPro";
import DataGridProToolbar from "../../components/DataGridProToolbar";
import Header from "../../components/Header";
import { useSavedColumnWidths } from "../../hooks/use-saved-column-widths";
import useUserInfoColumn from "../../hooks/use-user-info-column";
import { useSelector } from "../../store";
import { useListActivityLogsQuery } from "../../store/enhancedApi";
import { createdAt } from "../../utils/data-grid-col-defs";
import EntityDescription from "./EntityDescription";
import DiffButton from "./components/DiffButton";

const defaultColumns = JSON.stringify({
  id: false,
  "namespace.name": false,
  "user.id": false,
});

const DetailsList: FC = () => {
  const params = useParams();
  const changedBy = useUserInfoColumn("Change By");
  const { clusterId, organizationId } = useSelector((state) => state.current);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<string>(
    localStorage.getItem("deploymentDetailsColumns") || defaultColumns,
  );
  const { mutateColumnWidths, onColumnWidthChange } =
    useSavedColumnWidths("details");

  const changesetId = params.changesetId;

  const {
    data: activityLogsResult,
    isLoading,
    isFetching,
  } = useListActivityLogsQuery(
    {
      organizationId,
      changesetId: changesetId || "",
    },
    {
      skip: !organizationId || !changesetId,
    },
  );

  if (!changesetId) {
    return (
      <Alert severity="error">Missing changesetId parameter in the URL!</Alert>
    );
  }

  const onlyEqualsFilterOperators = getGridStringOperators().filter(
    ({ value }) => ["equals"].includes(value),
  );
  const activityLogs = activityLogsResult?.rows || [];

  let columns: GridColDef[] = [
    {
      headerName: "Id",
      field: "id",
      flex: 1,
    },
    {
      headerName: "Resource Id",
      field: "entity.id",
      flex: 2,
      filterOperators: onlyEqualsFilterOperators,
      valueGetter: (_value, row) => get(row, "entity.id"),
    },
    {
      headerName: "Resource Type",
      field: "entity.type",
      flex: 1,
      filterOperators: onlyEqualsFilterOperators,
      valueGetter: (_value, row) => get(row, "entity.type"),
    },
    {
      headerName: "Resource Name",
      field: "entity.name",
      flex: 2,
      filterOperators: onlyEqualsFilterOperators,
      valueGetter: (_value, row) => get(row, "entity.data.name"),
      renderCell: (params: GridCellParams) => {
        return <EntityDescription activityLog={params.row} />;
      },
    },
    {
      headerName: "Activity Type",
      field: "activityType",
      flex: 1,
      filterOperators: onlyEqualsFilterOperators,
    },
    {
      headerName: "Cluster",
      field: "namespace.name",
      flex: 1,
      valueGetter: (_value, row) => get(row, "namespace.name"),
    },
    changedBy,
    createdAt,
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      type: "actions",
      flex: 1,
      getActions: function action(params) {
        const actions = [
          <DiffButton key={`${params.row.id}`} activityLog={params.row} />,
        ];
        return actions;
      },
    },
  ];
  columns = mutateColumnWidths(columns);

  return (
    <>
      <Header title="Change Set Details" breadcrumbs={true} />
      <Container component={Paper} maxWidth={false} disableGutters={true}>
        <DataGridPro
          checkboxSelection={false}
          loading={isLoading || isFetching}
          rows={clusterId ? activityLogs || [] : []}
          columns={columns}
          columnVisibilityModel={JSON.parse(columnVisibilityModel)}
          onColumnVisibilityModelChange={(newModel) => {
            localStorage.setItem(
              "deploymentDetailsColumns",
              JSON.stringify(newModel),
            );
            setColumnVisibilityModel(JSON.stringify(newModel));
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: "updatedAt", sort: "desc" }],
            },
          }}
          slots={{
            toolbar: DataGridProToolbar,
          }}
          onColumnWidthChange={onColumnWidthChange}
        />
      </Container>
    </>
  );
};
export default DetailsList;
